'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import {
  Upload,
  Loader2,
  X,
  Copy,
  ChevronLeft,
  ChevronRight,
  DownloadIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { nanoid } from 'nanoid'

import { cn, generateCdnImageUrl } from '@/lib/utils'
import { uploadBlobToCdn } from '@/lib/utils'
import { BACKEND_API_URL } from '@/lib/constant'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { LinkedInIcon, TwitterIcon } from '@/components/icons'

interface SceneOption {
  id: string
  idForAPI: string
  image: string
}

const scenes: SceneOption[] = [
  {
    id: 'nasdaq',
    idForAPI: 'NASDAQ IPO',
    image: 'https://heyboss.heeyo.ai/preview/NASDAQ IPO Preview.png',
  },
  {
    id: 'forbes',
    idForAPI: 'Forbes Cover',
    image: 'https://heyboss.heeyo.ai/preview/Forbes Cover Preview.png',
  },
  {
    id: 'starship',
    idForAPI: 'AI Team at Starship',
    image: 'https://heyboss.heeyo.ai/preview/AI Team at Starship Preview.png',
  },
  {
    id: 'nbaFinals',
    idForAPI: 'NBA Finals',
    image: 'https://heyboss.heeyo.ai/preview/NBA Finals Preview.png',
  },
  {
    id: 'oscar',
    idForAPI: 'Oscar Ceremony',
    image: 'https://heyboss.heeyo.ai/preview/Oscar Ceremony Preview.png',
  },
  {
    id: 'presidentialOath',
    idForAPI: 'Presidential Oath',
    image: 'https://heyboss.heeyo.ai/preview/Presidential Oath Preview.png',
  },
  {
    id: 'privateJet',
    idForAPI: 'Private Jet',
    image: 'https://heyboss.heeyo.ai/preview/Private Jet Preview.png',
  },
  {
    id: 'redCarpet',
    idForAPI: 'Red Carpet',
    image: 'https://heyboss.heeyo.ai/preview/Red Carpet Preview.png',
  },
  {
    id: 'goldenHq',
    idForAPI: 'The Golden HQ',
    image: 'https://heyboss.heeyo.ai/preview/The Golden HQ Preview.png',
  },
]

// 只允许图片文件格式
const ALLOWED_IMAGE_FORMATS = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]

const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif']

export function AiTeamPhotoModal() {
  const t = useTranslations('aiTeamPhoto')
  const [selectedScene, setSelectedScene] = useState(scenes[0].id)
  const [uploadedImage, setUploadedImage] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  )
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sceneRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [referralCode, setReferralCode] = useState<string | null>(null)

  // 当选中场景变化时，自动滚动到对应位置
  useEffect(() => {
    const selectedElement = sceneRefs.current[selectedScene]
    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  }, [selectedScene])

  // 检查滚动状态
  const checkScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    const canLeft = scrollLeft > 5
    const canRight = scrollLeft < scrollWidth - clientWidth - 5

    setCanScrollLeft(canLeft)
    setCanScrollRight(canRight)
  }, [])

  // 监听滚动事件和尺寸变化
  useEffect(() => {
    const setupScrollListeners = () => {
      const container = scrollContainerRef.current
      if (!container) return null

      const handleScroll = () => {
        checkScrollButtons()
      }

      const resizeObserver = new ResizeObserver(() => {
        checkScrollButtons()
      })

      container.addEventListener('scroll', handleScroll)
      resizeObserver.observe(container)

      // 初始检查
      checkScrollButtons()

      return () => {
        container.removeEventListener('scroll', handleScroll)
        resizeObserver.disconnect()
      }
    }

    // 立即尝试设置
    const cleanup = setupScrollListeners()
    if (cleanup) return cleanup

    // 如果容器还没有渲染，延迟重试
    const timer = setTimeout(() => {
      const retryCleanup = setupScrollListeners()
      if (retryCleanup) return retryCleanup
    }, 100)

    return () => clearTimeout(timer)
  }, [checkScrollButtons])

  // 向左滚动
  const scrollLeft = () => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = container.clientWidth * 0.8
    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    })
  }

  // 向右滚动
  const scrollRight = () => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = container.clientWidth * 0.8
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    })
  }

  // 验证文件是否为图片
  const validateImageFile = (file: File): boolean => {
    const fileType = file.type
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`

    const isValidType = ALLOWED_IMAGE_FORMATS.includes(fileType)
    const isValidExtension = ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)

    return isValidType || isValidExtension
  }

  const handleImageUpload = async (file: File) => {
    // 验证文件类型
    if (!validateImageFile(file)) {
      toast.error(t('invalidFileType'))
      return
    }

    // 验证文件大小（10MB）
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('fileTooLarge'))
      return
    }

    setUploadedImage(file)

    // 创建预览 URL
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string)
    }
    reader.readAsDataURL(file)

    // 上传到 CDN
    setIsUploading(true)
    try {
      const extension = file.name.split('.').pop()
      const filename = `ai-team-user-upload/${nanoid()}.${extension}`

      const url = await uploadBlobToCdn(file, filename, file.type)
      setUploadedUrl(url)
      toast.success(t('uploadSuccess'))
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error(t('uploadError'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (file) {
      handleImageUpload(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    const imageFile = droppedFiles.find((file) => validateImageFile(file))

    if (imageFile) {
      handleImageUpload(imageFile)
    } else {
      toast.error(t('invalidFileType'))
    }
  }

  const handleRemoveImage = () => {
    setUploadedImage(null)
    setPreviewUrl(null)
    setUploadedUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCreatePhoto = async () => {
    if (!uploadedImage || !uploadedUrl) {
      toast.error(t('pleaseUploadFirst'))
      return
    }

    // 找到选中场景的 idForAPI 值
    const selectedSceneData = scenes.find((scene) => scene.id === selectedScene)
    if (!selectedSceneData) {
      toast.error(t('sceneNotFound'))
      return
    }

    setIsCreating(true)
    toast.info(t('creatingPhoto'))
    try {
      // 调用 AI 生成服务
      const response = await fetch(`${BACKEND_API_URL}/v1/composite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          photo: uploadedUrl,
          scene: selectedSceneData.idForAPI,
        }),
      })

      const result = await response.json()

      if (result.code === 200 && result.data?.image) {
        // 成功生成
        setGeneratedImageUrl(result.data.image)
        setIsCompleted(true)
        toast.success(t('photoCreated'))

        // Track successful generation
        sendToBothAnalytics(
          MIXPANEL_CLIENT_EVENT_NAMES.AI_TEAM_PHOTO_GENERATE_SUCCESS,
          {
            scene: selectedSceneData.idForAPI,
          },
        )

        // 保存 AI 团队照片到用户数据
        try {
          const saveResponse = await fetch('/api/me/ai-team-photo', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ai_team_photo: result.data.image }),
          })

          const saveResult = await saveResponse.json()
          if (!saveResult.success) {
            console.error(
              'Failed to save AI team photo to user data:',
              saveResult.error,
            )
          }

          await fetch('/api/referral/stats')
            .then((res) => res.json())
            .then((data) => {
              if (data.referralCode) {
                setReferralCode(data.referralCode)
              }
            })
        } catch (error) {
          console.error('Error saving AI team photo to user data:', error)
        }
      } else {
        // 生成失败
        const errorInfo = result.data?.info || result.message || 'Unknown error'

        if (errorInfo === 'no face detected') {
          toast.error(t('noFaceDetected'))
        } else {
          toast.error(`${t('createError')}: ${errorInfo}`)
        }
      }
    } catch (error) {
      console.error('Create photo failed:', error)
      toast.error(t('networkError'))
    } finally {
      setIsCreating(false)
    }
  }

  const handleShare = (platform: 'twitter' | 'linkedin' | 'copy') => {
    if (!generatedImageUrl) return

    const shareText = t('shareText')
    const shareUrl = `${window.location.origin}/offer/${referralCode}`

    switch (platform) {
      case 'twitter': {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
        window.open(twitterUrl, '_blank')
        // Track Twitter share
        sendToBothAnalytics(
          MIXPANEL_CLIENT_EVENT_NAMES.AI_TEAM_PHOTO_SHARE_TWITTER,
          {
            referral_code: referralCode,
          },
        )
        break
      }
      case 'linkedin': {
        const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`
        window.open(linkedinUrl, '_blank')
        // Track LinkedIn share
        sendToBothAnalytics(
          MIXPANEL_CLIENT_EVENT_NAMES.AI_TEAM_PHOTO_SHARE_LINKEDIN,
          {
            referral_code: referralCode,
          },
        )
        break
      }
      case 'copy': {
        navigator.clipboard.writeText(shareUrl)
        toast.success(t('linkCopied'))
        break
      }
    }
  }

  const handleRestart = () => {
    setIsCompleted(false)
    setGeneratedImageUrl(null)
    setUploadedImage(null)
    setPreviewUrl(null)
    setUploadedUrl(null)
    setSelectedScene(scenes[0].id)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDownload = async () => {
    if (!generatedImageUrl) return

    try {
      // 获取图片数据
      const response = await fetch(generatedImageUrl)
      const blob = await response.blob()

      // 创建 blob URL
      const blobUrl = window.URL.createObjectURL(blob)

      // 创建下载链接
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `ai-team-photo-${nanoid()}.png`
      document.body.appendChild(a)
      a.click()

      // 清理
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)

      // Track download
      sendToBothAnalytics(MIXPANEL_CLIENT_EVENT_NAMES.AI_TEAM_PHOTO_DOWNLOAD)
    } catch (error) {
      console.error('Download failed:', error)
      toast.error(t('downloadError'))
    }
  }

  // 如果已完成，显示结果页面
  if (isCompleted && generatedImageUrl) {
    return (
      <DialogContent
        className="w-[95%] max-w-[640px] p-0 rounded-[20px] overflow-hidden border border-[#3B3B3B]"
        style={{
          background:
            'linear-gradient(104deg, #222 1.87%, #2B2B2B 56.52%, #010101 111.18%)',
          boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="relative w-full max-h-[95vh] overflow-y-auto">
          <div className="p-4 md:p-6">
            {/* Header */}
            <div className="mb-4">
              <h1 className="text-lg md:text-2xl font-bold text-white mb-2">
                🎉 {t('photoReady')}
              </h1>
              <p className="text-sm md:text-base text-gray-300">
                {t('photoReadyDescription')}
              </p>
            </div>

            {/* Generated Image */}
            <div className="mb-4">
              <div className="relative rounded-xl overflow-hidden border-2 border-white/10">
                <img src={generatedImageUrl} alt={t('generatedPhoto')} />
              </div>
            </div>

            {/* Share Text */}
            <div className="mb-4">
              <p className="text-sm md:text-base text-gray-300">
                {t('shareDescription')}
              </p>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                size="lg"
                onClick={() => handleShare('twitter')}
                className="flex-1 bg-black hover:bg-black/40 text-white font-normal transition-all duration-200 flex items-center justify-center gap-2"
              >
                <TwitterIcon className="w-6 h-6" />
                <span className="text-base leading-8">X (Twitter)</span>
              </Button>
              <Button
                size="lg"
                onClick={() => handleShare('linkedin')}
                className="flex-1 bg-[#0387cf] hover:bg-[#0387cf]/80 text-white font-normal transition-all duration-200 flex items-center justify-center gap-2"
              >
                <LinkedInIcon className="w-6 h-6" />
                <span className="text-base leading-8 capitalize">linkedin</span>
              </Button>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Create Another Button */}
              <Button
                onClick={handleRestart}
                className="flex-1 py-3 font-semibold bg-white text-black hover:bg-gray-100 transition-colors"
              >
                {t('createNew')}
              </Button>
              <Button
                variant="transparent"
                onClick={handleDownload}
                className="flex-1"
                disabled={!generatedImageUrl}
              >
                <DownloadIcon className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button
                variant="transparent"
                onClick={() => handleShare('copy')}
                className="flex-1"
              >
                <Copy className="w-4 h-4 mr-2" />
                {t('copyLink')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    )
  }

  return (
    <DialogContent
      className="w-[95%] max-w-[640px] p-0 rounded-[20px] overflow-hidden border border-[#3B3B3B]"
      style={{
        background:
          'linear-gradient(104deg, #222 1.87%, #2B2B2B 56.52%, #010101 111.18%)',
        boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
      onPointerDownOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <div className="relative w-full max-h-[95vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          {/* Header */}
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl">
              {t('title')}
            </DialogTitle>
          </DialogHeader>

          {/* Scene Selection */}
          <div className="mt-4 md:mt-6 mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-semibold text-white mb-2">
              {t('selectScene')}
            </h2>
            <p className="text-sm md:text-base text-gray-300 mb-2">
              {t('sceneDescription')}
            </p>

            {/* 横向滚动容器 */}
            <div className="relative">
              {/* 左滚动按钮 */}
              {canScrollLeft && (
                <button
                  type="button"
                  onClick={scrollLeft}
                  className="absolute -left-2 top-[45%] -translate-y-1/2 z-20 w-10 h-10 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-lg"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}

              {/* 右滚动按钮 */}
              {canScrollRight && (
                <button
                  type="button"
                  onClick={scrollRight}
                  className="absolute -right-2 top-[45%] -translate-y-1/2 z-20 w-10 h-10 bg-black/50 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-lg"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}

              <div
                ref={scrollContainerRef}
                className="-mx-4 md:-mx-6 px-4 md:px-6 pt-4 relative max-w-[640px] overflow-x-auto snap-x"
              >
                <div className="flex gap-4 md:gap-6 pb-4">
                  {scenes.map((scene) => (
                    <div
                      key={scene.id}
                      ref={(el) => {
                        sceneRefs.current[scene.id] = el
                      }}
                      className="flex-shrink-0 w-[180px] md:w-[240px] snap-center"
                    >
                      <input
                        type="radio"
                        id={`scene-${scene.id}`}
                        name="scene"
                        value={scene.id}
                        checked={selectedScene === scene.id}
                        onChange={() => setSelectedScene(scene.id)}
                        className="sr-only"
                      />
                      <label
                        htmlFor={`scene-${scene.id}`}
                        className="block cursor-pointer transition-all duration-200"
                      >
                        {/* Image Container */}
                        <div
                          className={cn(
                            'relative rounded-lg overflow-hidden mb-3 transition-all duration-200',
                            selectedScene === scene.id && 'ring-2 ring-brand_2',
                          )}
                        >
                          <div className="aspect-[3/2] relative">
                            <img
                              src={generateCdnImageUrl(scene.image, {
                                width: 480,
                              })}
                              alt={t(`scenes.${scene.id}`)}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {/* 半透明遮罩，选中时消失 */}
                            <div
                              className={cn(
                                'absolute inset-0 bg-black/20 transition-opacity duration-200',
                                selectedScene === scene.id
                                  ? 'opacity-0'
                                  : 'opacity-100',
                              )}
                            />
                          </div>
                        </div>

                        {/* Label Text with Selection Indicator */}
                        <div className="flex items-center justify-center gap-2">
                          {/* Selection Indicator */}
                          <div
                            className={cn(
                              'w-4 h-4 rounded-full border transition-all duration-200 flex items-center justify-center',
                              selectedScene === scene.id
                                ? 'border-brand_2'
                                : 'border-white',
                            )}
                          >
                            {selectedScene === scene.id && (
                              <div className="w-2 h-2 bg-brand_2 rounded-full" />
                            )}
                          </div>

                          <span
                            className={cn(
                              'text-sm font-medium transition-colors duration-200',
                              selectedScene === scene.id
                                ? 'text-brand_2'
                                : 'text-gray-300',
                            )}
                          >
                            {t(`scenes.${scene.id}`)}
                          </span>
                        </div>
                      </label>
                    </div>
                  ))}
                  <div className="flex-shrink-0 w-1" />
                </div>
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-xl font-semibold text-white mb-2">
              {t('uploadPhoto')}
            </h2>
            <p className="text-sm md:text-base text-gray-300 mb-4 md:mb-6">
              {t('uploadDescription')}
            </p>

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'flex flex-col items-center justify-center w-full h-48 md:h-64 rounded-xl cursor-pointer transition-all duration-200',
                'bg-white/[0.04] border-2 border-dashed',
                isDragging
                  ? 'border-brand_2 bg-white/[0.08]'
                  : 'border-white/40 hover:bg-white/[0.06] hover:border-white/50',
              )}
              onClick={() => !previewUrl && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !previewUrl) {
                  e.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              aria-label={t('uploadPlaceholder')}
            >
              {previewUrl ? (
                <div className="relative w-full h-full p-4">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveImage()
                    }}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                      <div className="flex items-center space-x-2 text-white">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>{t('uploading')}</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center pt-4 md:pt-5 pb-4 md:pb-6 px-4">
                  <Upload className="w-8 md:w-10 h-8 md:h-10 mb-2 md:mb-3 text-white" />
                  <p className="mb-1 md:mb-2 text-base md:text-lg text-white font-medium text-center">
                    {t('uploadPlaceholder')}
                  </p>
                  <p className="text-xs md:text-sm text-white/70 text-center">
                    {t('fileFormat')}
                  </p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={ALLOWED_IMAGE_FORMATS.join(',')}
                onChange={handleFileInputChange}
              />
            </div>
          </div>

          {/* Create Button */}
          <Button
            onClick={handleCreatePhoto}
            disabled={!uploadedUrl || isCreating}
            className="w-full py-4 font-semibold bg-white text-black hover:bg-gray-100 disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('creating')}
              </>
            ) : (
              t('createButton')
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  )
}
